/** Value Dashboard domain types */

import type { CfbBucket } from '../../lib/cfb/subdivision';
// Type-only: the runtime direction is dashboardSport -> types, so importing the
// value here would close a cycle. Erased at build.
import type { CfbLandingBoard } from '../../lib/routing/cfbLandingBoard';
import type { KalshiPriceRange } from '../../types';
import type { MarketFreshness } from '../../lib/sport/marketFreshness';
import type { AdvancePromotionEvidence } from '../../lib/advancePromotion';
import type {
  KalshiExchangeIndexState,
} from '../../lib/kalshiExchangeIndex';

export type Sport = 'nba' | 'nfl' | 'wnba' | 'nhl' | 'cbb' | 'cfb' | 'atp' | 'wta' | 'mlb' | 'ufc' | 'esports' | 'boxing' | 'cricket' | 'soccer' | 'golf';

/** Sports rendered as exact standalone propositions, not away/home GameRows.
 * Golf futures remain under Futures. Dated Golf match winners have a separate
 * board, exposed only when explicitly identified match inventory is delivered. */
export const STANDALONE_SPORTS: ReadonlySet<Sport> = new Set<Sport>(['golf']);

/** Sports whose games are N-outcome (not the binary away/home GameRow) and so
 *  flow through the multi-outcome path (getMultiOutcomeEvents), not getGames.
 *  Soccer is 3-way (Home / Away / Draw). The binary dashboard table excludes
 *  these; they render in Live Discovery (and, later, a multi-outcome table). */
export const MULTI_OUTCOME_SPORTS: ReadonlySet<Sport> = new Set<Sport>(['soccer']);
export type MarketType = 'moneyline' | 'spread' | 'total' | 'spread-total' | 'series';
export type Venue = 'kalshi' | 'polymarket';
export type TeamSide = 'away' | 'home';
export type MarketSide = 'yes' | 'no';
/**
 * Which price each Y/N pill displays:
 *   maker  → highest standing bid on this side's ladder (post a resting order)
 *   taker  → lowest ask on this side (= 100 − best opposite-side bid)
 *
 * Last-trade price was previously surfaced via a 'market' mode; that's
 * gone in favor of a pulsing grey glow on the ladder row whose price
 * matches the venue's last trade (see pillPriceCents in ladder/helpers).
 */
export type PriceMode = 'maker' | 'taker' | 'market';
export type DateFilter = 'today' | 'all';
/**
 * #712 — how wide a bounded date view is on the NFL/CFB game boards.
 *
 * `day` is the long-standing single-date filter and stays the factory default.
 * `week` widens it to the inclusive Tuesday–Monday window containing the
 * selected date (#818). Orthogonal to `DateFilter`: this says how wide the bounded
 * view is, `DateFilter` says whether the view is bounded at all. See
 * `weeklySlate.ts` for the window itself and for which boards may have one.
 */
export type SlateRange = 'day' | 'week';
export type StatusFilter = 'active' | 'all' | 'settled';

export interface BookLevel {
  priceCents: number;
  size: number;
}

export interface VenueBook {
  marketTicker: string;
  /** Kalshi-only observed matching-engine identity. Zero is a real value. */
  exchangeIndex?: number;
  /**
   * Diagnostic state for the optional identity. Missing/malformed/conflicting
   * producer evidence stays distinct and is never guessed into shard zero.
   */
  exchangeIndexState?: KalshiExchangeIndexState;
  /** True when this display book is the native market's NO complement.
   *  The visible YES/NO ladders are already swapped, but history stores such
   *  as depth velocity remain keyed to the venue-native side. Consumers that
   *  read those stores must flip the requested side when this is true. */
  nativeSideInverted?: boolean;
  /** Per-ticker observation verdict. Undefined is the backwards-compatible
   *  representation of 'unknown' for synthetic/static books. */
  freshness?: MarketFreshness;
  /** Present only for feeds using the negotiated continuity contract. */
  sourceValidity?: 'continuous' | 'unconfirmed';
  /** Book proof is independent of ticker price proof. */
  bookSourceValidity?: 'continuous' | 'unconfirmed';
  /** Last-confirmed, display-only depth during a bounded same-generation
   * maintenance resync. Never an order-sizing or action input. */
  retainedDepth?: { yes: BookLevel[]; no: BookLevel[]; liquidity: number; confirmedAtMs: number };
  /** Raw hydra market status ('open' | 'active' | 'closed' | …). Feeds the
   *  LT2 line-availability classifier (suspended detection). Undefined on
   *  synthetic/static books and pre-status frames — treated as unknown,
   *  never as suspended. */
  status?: string;
  /** Polymarket-only. The outcome token whose price this book displays as
   *  YES — i.e. the token a "BUY YES" click on this book should target. */
  tokenId?: string;
  /** Polymarket-only. The outcome token whose price this book displays as
   *  NO — i.e. the token a "BUY NO" click on this book should target.
   *
   *  For moneyline this is the OTHER team's YES outcome (same conditionId,
   *  opposite asset_id). For spread/total, where the away/home slots share
   *  one underlying book, this is the slug's NO-side asset_id on the same
   *  market. Either way the TradeModal can dispatch using just the
   *  selected book — no need to look up the opposite team's book. */
  noTokenId?: string;
  conditionId?: string;
  /** Polymarket-only. True when the market is part of a NEG_RISK
   *  multi-outcome event (mutually-exclusive bundled yes/no markets —
   *  NBA Draft first pick, election primary slots, etc.). TradeModal
   *  forwards this to placeOrder via the v2 SDK; without it Polymarket
   *  rejects the order with "invalid signature". Undefined on kalshi
   *  books and standard binary poly markets (treated as false). */
  negRisk?: boolean;
  /** Polymarket-only. CLOB minimum price increment as a stringified
   *  decimal — "0.01" for most sports moneylines, "0.001"/"0.0001" for
   *  fine-tick binaries. Forwarded from `HydraMarket.tickSize` (Gamma
   *  `orderPriceMinTickSize` plumbed through hydra's CachedMarket).
   *  TradeModal passes this to placeOrder so the v2 CLOB doesn't reject
   *  with `invalid tick size`. Undefined on kalshi books and on poly
   *  markets where hydra (older builds) or Gamma omits the field; the
   *  relay falls back to '0.01' in that case, which is correct for sports
   *  moneylines but wrong for fine-tick markets — forward when present. */
  tickSize?: string;
  /** Kalshi-only authoritative banded order-price grid from Hydra. */
  priceRanges?: KalshiPriceRange[];
  priceCents: number | null;
  lastPriceCents?: number | null;
  /** Frame BBO scalars (market.yesBid / market.yesAsk), sentinel-guarded at
   *  build (0-bid / 100-ask empty-side encodings become null). The pill
   *  layer's FALLBACK for bookless markets — static Poly props carry no
   *  orderbook (never WS-subscribed) and often no last trade, so without
   *  these every price mode rendered "—" (poly-props-events, 2026-07-17).
   *  Orderbook levels always take precedence when present: preferring the
   *  scalar over the book is the f899b43 frozen-pill class — don't. */
  yesBidCents?: number | null;
  yesAskCents?: number | null;
  volume: number;
  /** Per-side resting depth (sum of contract size on each book side).
   *  `yesVolume` is the YES-side ladder (bids on YES); for totals this
   *  reads as Over demand. `noVolume` is the NO-side ladder; for totals
   *  this reads as Under demand. Distinct from `volume` (which is
   *  cumulative traded volume from the venue, or fallback aggregate depth
   *  when the venue hasn't sent a market frame). Used by the
   *  spread/total table to surface the O / U liquidity split that the
   *  aggregate K Vol column hides. Undefined when the book has no
   *  orderbook frame yet — the cell renders an em-dash in that case. */
  yesVolume?: number;
  noVolume?: number;
  /** #154 — truthfulness cue: `volume` did NOT come from a venue-reported
   *  cumulative TRADED volume (`mkt.volume`), but from the YES+NO resting-depth
   *  FALLBACK in `buildVenueBookFromMarket` (hydra sent no market frame yet).
   *  When true, a renderer showing `volume` under a "Vol" header must mark it as
   *  RESTING DEPTH — not executed volume — so the two aren't conflated. Undefined
   *  / false = `volume` is real traded volume. Presentation flag only; the number
   *  itself is unchanged. */
  volumeIsDepthDerived?: boolean;
  /** #154 (D3) — truthfulness cue: this book is ONE side of a single shared
   *  binary book, so BOTH its `volume` AND `liquidity` are the whole-book
   *  aggregate, not an independent per-outcome total. Set in two places:
   *    - `buildPolyMoneylinePair` — away + home read the same conditionId, and
   *      BOTH rows render, so the figure is visibly duplicated down the table.
   *    - the poly spread/total lines — Over/Under are the two sides of one
   *      binary market. Here the figure is rendered ONCE per row
   *      (SpreadsTotalsTable emits one `<tr>` per line; GameDetail's synthesized
   *      rows resolve to a single `singleSide`), so nothing is duplicated on
   *      screen — but the number is still a whole-book total rather than that
   *      outcome's own, which is exactly what the cue has to say.
   *  So the flag means "this is the WHOLE BOOK's total", NOT "this appears
   *  twice" — the tooltips are worded to claim only the former, because the
   *  latter is false on every surface except the moneyline pair.
   *  Undefined / false = an independent per-book total. Presentation flag only;
   *  the numbers themselves are unchanged. Generalizes the earlier volume-only
   *  `volumeIsShared` to cover liquidity too (the #154 gap Brendan flagged). */
  isSharedBinaryBook?: boolean;
  liquidity: number;
  yes: BookLevel[];
  no: BookLevel[];
}

export interface GameRow {
  key: string;
  sport: Sport;
  date: string;        // YYYY-MM-DD; sentinel for playoff series rows (no real date)
  timePt: string;      // "7:00p" — local-time format of startTimeMs; empty when unknown
  /** Scheduled event start epoch ms from hydra's eventStartTime
   *  (Polymarket Gamma or Kalshi milestones). Null when no source delivered
   *  it. Sort treats null as +Infinity so unknown-start rows sink within
   *  their date bucket. */
  startTimeMs: number | null;
  /** 'live' = at least one source's market is currently 'open' (book trading).
   *  'final' = all sources are in TERMINAL_STATUSES. 'pre' = listed but not
   *  yet trading (status 'active' / 'initialized' / unknown). Series rows
   *  always 'live' or 'pre' — never 'final' (this row doesn't render the
   *  series outcome, just the underlying tickers' liveness). */
  gameState: 'live' | 'pre' | 'final';
  /** Source class behind gameState. Trading warnings must not treat a
   * clock-coerced final or clock-started live row as authoritative. */
  gameStateAuthority?: 'venue' | 'schedule' | 'heuristic' | 'unknown';
  /** Side that resolved YES on a final game ('away' | 'home'), or null while
   *  the game isn't final / can't be derived. Drives the ✓ winner mark on
   *  FINAL rows. Derived from whichever team's market reports lastPriceCents
   *  at 100¢. */
  finalWinnerSide: 'away' | 'home' | null;
  /** Soccer knockout "To Advance" rows only (`<gameKeyV2>|advance`, from
   *  marketStore.getAdvanceGame): the promotion evidence the store built from
   *  the raw frames, the same object the dashboard event carries. Consumers
   *  decide headline/demotion via `advancePromotes(row.advancePromotion)`,
   *  never from the rendered `kalshi` pair (which needs BOTH sides). */
  advancePromotion?: AdvancePromotionEvidence;
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  awayRotation?: number | null;
  homeRotation?: number | null;
  /**
   * Set when this row represents a Kalshi playoff-series-winner market
   * (`KX{NBA|NHL|MLB}SERIES-...`). The two outcome tickers (one per team)
   * still flow through `kalshi.away` / `kalshi.home`, but this row spans
   * the entire series rather than a single calendar day.
   */
  series?: { round: number; year: string };
  /**
   * Set on a per-day game row when Kalshi has dropped the per-day game
   * ticker for a possible series-clincher and only the series-winner
   * tickers exist for the same teams. The row's `kalshiAwayTicker` /
   * `kalshiHomeTicker` get populated with the SERIES-{TEAM} tickers so
   * the row renders normally; this flag tells the UI to add a "Clincher"
   * badge and a tooltip clarifying that pill clicks settle on series
   * outcome, not tonight's game.
   */
  kalshiViaSeries?: { round: number; year: string };
  /**
   * Tennis sibling of kalshiViaSeries. Kalshi lists no match market for a
   * tournament FINAL — the tournament-winner event, narrowed to two live
   * legs, IS the match. The row's Kalshi books are those legs; the flag
   * drives the trade-modal callout (settles on the tournament winner,
   * which this final decides).
   */
  kalshiViaTournament?: { eventTicker: string };
  kalshi?: {
    away: VenueBook;
    home: VenueBook;
  } | null;
  polymarket?: {
    away: VenueBook;
    home: VenueBook;
  } | null;
  /** Set on rows projected from /api/analytics/games for closed games
   *  beyond hydra's WS-cache prune window. The renderer collapses these
   *  to a single skinny row (date + teams + per-source volume) instead
   *  of the standard away/home pair. kalshi/polymarket are null on
   *  archived rows — there's no live book to show. */
  archived?: {
    kalshiVolume: number;
    polyVolume: number;
  };
  /** Present only on rows minted by synthesizeFromSpread. Carries the
   *  SpreadLine authority the caption layer needs (#555): without it the
   *  synth row's duplicated away/home books leave no way to know which
   *  signed name is the market's YES outcome. */
  spreadOutcome?: SyntheticSpreadOutcome;
}

/** The exact-line identity a synthetic spread GameRow was minted from.
 *  Describes the frontend's synthesized row only — not a venue-wide market
 *  schema. Don't add this to VenueBook, market-store ingress, or other
 *  market kinds. */
export interface SyntheticSpreadOutcome {
  /** Signed from the away team's perspective: negative = away favored,
   *  positive = home favored. Same convention as SpreadLine.line. */
  line: number;
  favored: TeamSide;
  /** Exact attached books for this synthesized line; null means that venue
   *  is absent. */
  kalshiTicker: string | null;
  polymarketTicker: string | null;
}

/** Everything a trade-ticket surface needs to seed an order entry for one
 *  team/venue slot of a GameRow. Born in the retired TradeTicketBody (the
 *  pre-2026-05-22 modal lane); lives on as the shape GameDetail's sections
 *  and SpreadsTotals hand to the GameDetailPanel tradeIntent wiring. */
export interface TradeTicketContext {
  game: GameRow;
  teamSide: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
  prefillPrice?: number;
  initialOrderSide?: 'yes' | 'no';
  postOnly: boolean;
  /** D-020 — click provenance from the spread/total renderers
   *  ({origin: 'pill' | 'ladder', kind?: 'bid' | 'ask'}). Consumed ONLY by
   *  the LT2 one-click interception layer to derive maker semantics; the
   *  ticket itself ignores it, so flag-off payload behavior is unchanged. */
  meta?: { origin: 'pill' | 'ladder'; kind?: 'bid' | 'ask'; lineIdx?: number };
}

export interface SportTab {
  id: Sport;
  label: string;
}

export interface MarketTypeTab {
  id: MarketType;
  label: string;
}

export const SPORT_TABS: SportTab[] = [
  { id: 'nba', label: 'NBA' },
  { id: 'nfl', label: 'NFL' },
  { id: 'wnba', label: 'WNBA' },
  { id: 'nhl', label: 'NHL' },
  { id: 'cbb', label: 'CBB' },
  { id: 'cfb', label: 'CFB' },
  { id: 'atp', label: 'ATP' },
  { id: 'wta', label: 'WTA' },
  { id: 'mlb', label: 'MLB' },
  { id: 'ufc', label: 'UFC' },
  { id: 'esports', label: 'Esports' },
  { id: 'boxing', label: 'Boxing' },
  { id: 'cricket', label: 'Cricket' },
  { id: 'soccer', label: 'Soccer' },
];

/** Names include futures-only sports without adding them to game inventories. */
export const SPORT_LABEL = {
  ...Object.fromEntries(SPORT_TABS.map((t) => [t.id, t.label])),
  golf: 'Golf',
} as Record<Sport, string>;

/** CFB subdivision bucket → display label (#653). Sibling of SPORT_LABEL: the
 *  toolbar's bucket chips and the table header read the SAME word for a
 *  bucket, so what you clicked is what the header says. */
export const CFB_BUCKET_LABEL: Record<CfbBucket, string> = {
  fbs: 'FBS',
  fcs: 'FCS',
  other: 'Other',
};

/** The four CFB boards a trader can be on, in the order the toolbar renders
 *  them, with the word each one is called. 'All CFB' leads so the parent is
 *  named inside the child control and the unfiltered slate stays one click
 *  away.
 *
 *  One list rather than two (#728): the toolbar's bucket chips and the
 *  Settings landing-default row offer the SAME four choices, and a trader who
 *  picks "FBS" in Settings must find that word on the chip they land on.
 *  Deliberately NOT part of SPORT_TABS or DASHBOARD_SPORT_TABS — a bucket is
 *  not a sport and must never appear in the sport row. */
export const CFB_BOARD_TABS: ReadonlyArray<{ id: CfbLandingBoard; label: string }> = [
  { id: 'cfb', label: `All ${SPORT_LABEL.cfb}` },
  { id: 'cfb-fbs', label: CFB_BUCKET_LABEL.fbs },
  { id: 'cfb-fcs', label: CFB_BUCKET_LABEL.fcs },
  { id: 'cfb-other', label: CFB_BUCKET_LABEL.other },
];

export const MARKET_TYPE_TABS: MarketTypeTab[] = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread-total', label: 'Spread/Total' },
  { id: 'series', label: 'Futures' },
];

/**
 * Series (championship) outcome — one ticker per candidate team.
 * Shape: KXNBA-26-LAL / KXNHL-26-EDM / KXMLB-26-SEA
 */
export interface SeriesOutcome {
  ticker: string;
  code: string;
  name: string;
  kalshi: VenueBook | null;
  polymarket?: VenueBook | null;
  role?: 'away' | 'home' | 'draw' | 'other';
}

export interface SeriesEvent {
  key: string;            // `${sport}|${year}`; tennis tournaments append `|${seg}` (wta|26|WIM)
  sport: Sport;
  year: string;           // "26"
  seriesTicker: string;   // "KXNBA"
  title: string;          // "NBA 2026 Champion"
  outcomes: SeriesOutcome[];
}

/**
 * One leg of a futures board — a single binary "will X happen" market with no
 * game identity. Deliberately flatter than SeriesOutcome: futures legs have no
 * team code, no cross-venue pair (these are Polymarket-only today), and no
 * orderbook (the shallow tier serves static Gamma prices refreshed per sweep,
 * never a WS book), so a VenueBook would be mostly empty fields.
 */
export interface FutureLeg {
  ticker: string;
  /** Display name of the subject: player, team, coach. */
  name: string;
  /** Sub-market inside the board — 'Winner' / 'Top 5' on a tournament,
   *  the line ('6.5') on a totals-style family, null when the board IS the
   *  market. */
  kindLabel: string | null;
  /** Cents. Null = the feed sent no price — render it as absent, never 0. */
  yesBid: number | null;
  yesAsk: number | null;
  /** Mid of bid/ask when both are present, else last trade. Sort key. */
  priceCents: number | null;
  lastPriceCents: number | null;
  volume: number | null;
  status: string;
  source: Venue;
}

/**
 * A futures container — one tournament / award / season family, holding every
 * leg that resolves against it. Built by marketStore.getFutureBoards from the
 * slug (hydraedge forwards no container id on future frames; see
 * lib/futures/parseFutureSlug).
 */
export interface FutureBoard {
  /** `${sport}|${containerKey}` — stable across sweeps. */
  key: string;
  sport: Sport;
  /** Display name, derived from the legs' own prose titles. */
  title: string;
  /** Tenant inside the sport (golf tour: 'PGA'/'LPGA'/…), null when the sport
   *  has no sub-tour. Drives the board's grouping chip. */
  tour: string | null;
  legs: FutureLeg[];
  /** Legs carrying a usable price. `0` with a non-empty `legs` means the feed
   *  is delivering this board unpriced — surfaced, not hidden. */
  pricedLegs: number;
  /** Summed leg volume; null when no leg carries volume. */
  volume: number | null;
  /** Most recent frame time across the legs (0 when none has been updated
   *  since ingest — static snapshots legitimately carry 0). */
  lastUpdateMs: number;
}

/**
 * A per-event market with N mutually-exclusive outcomes (soccer Home/Away/Draw
 * today; the politics/entertainment tail later). Generalizes SeriesEvent from
 * championship futures to any event-grouped multi-outcome market. Built by
 * marketStore.getMultiOutcomeEvents, grouped by canonicalGameKey when present.
 * Reuses SeriesOutcome for each outcome's {ticker, code, name, venue books}.
 */
export interface MultiOutcomeEvent {
  key: string;            // event ticker, e.g. "KXUECLGAME-26MAY27CRYRVC"
  sport: Sport;
  category: string;       // ticker category, e.g. "soccer-game"
  date: string | null;    // YYYY-MM-DD
  timePt: string;
  startTimeMs: number | null;
  gameState: 'live' | 'pre' | 'final';
  gameStateAuthority?: 'venue' | 'schedule' | 'heuristic' | 'unknown';
  title: string;          // "Crystal Palace vs Vallecano"
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  outcomes: SeriesOutcome[];
  /** Market flavor. Absent / 'winner' = the regulation result (soccer 1X2,
   *  Home/Away/Draw). 'advance' = the knockout "To Advance" 2-way (the two
   *  teams, no draw) — drives the panel's "does not advance" wording + a "To
   *  Advance" context label instead of "does not win". Built by
   *  marketStore.getAdvanceEvents. */
  kind?: 'winner' | 'advance';
  /** `kind: 'advance'` only: store-built promotion evidence (see the same
   *  field on GameRow). */
  advancePromotion?: AdvancePromotionEvidence;
}

/**
 * Per-line spread market for one event. Kalshi usually seeds one ticker per
 * (game, line, favored team) and Polymarket attaches when a matching slug
 * exists. Period lines may be venue-specific, so either venue can be absent.
 */
export interface SpreadLine {
  /**
   * Signed line from the away team's perspective. Negative = away covers
   * (away is the favorite at this line); positive = home covers. Magnitude
   * comes from Kalshi's `bucket − 0.5` convention — `TEAM<N>` resolves YES
   * when the team wins by N or more, i.e. covers a (N − 0.5) line. Switches
   * to floor_strike when hydra forwards it on WS frames.
   */
  line: number;
  favored: 'away' | 'home';
  kalshi?: VenueBook;
  /** Raw Poly book at the SAME line and covering side — exact strike
   *  identity after each venue's settlement conversion, never a nearby
   *  line (spreadMatch.ts). A Poly line Kalshi does not list is its own row. */
  polymarket?: VenueBook;
}

/**
 * Per-strike total market for one event. YES = Over, NO = Under. Kalshi
 * usually seeds the line, but period totals can be Polymarket-only.
 */
export interface TotalLine {
  line: number;            // always positive (strike − 0.5 fallback for now)
  kalshi?: VenueBook;
  polymarket?: VenueBook;
}

/**
 * One row per canonical game with all the line markets for that game grouped
 * together. Consumed by the Spreads & Totals view; future event-detail pages
 * read the same shape.
 */
/**
 * Period grain for a market. 'full' = whole game (the default everywhere).
 * '1H' = 1st half (Kalshi + Poly). '1Q'–'4Q' = single quarters (Kalshi-only —
 * Polymarket lists no quarter markets). Hydra stamps this on every WS frame
 * (`market.period`) and it's also derivable from the Kalshi series prefix.
 * IMPORTANT: a quarter resolves on that quarter in isolation — 2Q ≠ 1H (a
 * quarter total ≈54.5, a half total ≈109–114). Never pair a Poly 1H line onto
 * a Kalshi quarter. '2H' = 2nd half — ingested for soccer Game Lines (World Cup
 * 1H/2H spread/total/moneyline + BTTS); NBA/WNBA don't list a 2nd-half series.
 * 'F5' = MLB First 5 Innings (moneyline/spread/total), Kalshi-only — Polymarket
 * lists no inning markets, and F5 has no F3/F7 siblings. Like a quarter, F5
 * resolves on the first five innings in isolation, so it shares the full-game
 * canonicalGameKey but never the full-game lines.
 */
export type Period = 'full' | '1H' | '2H' | 'F5' | '1Q' | '2Q' | '3Q' | '4Q';

/** Full-feed B (hydracore 2026-08-17): an explicit OP bucket reference a
 *  row can pin its OP/PNCL cell to — period-scoped (vendor `period`
 *  string) or full-game (periodKey 1), with the row's line on line
 *  families. When present, GameRows reads THIS bucket instead of the
 *  full-game moneyline; a bucket with no data renders "—", never a
 *  fallback to a different market's price. */
export interface OpBucketRef {
  gameKey: string;
  periodKey: number | string;
  marketType: string;
  points?: number | null;
  /** op-player-props: REQUIRED when `marketType === 'player-prop'` — the
   *  bucket is one per (stat, player, line), and the player is the WIRE
   *  `playerKey` (core's canonical TEAM+CODE+jersey), never a ticker-derived
   *  or Poly identity. A prop bucket without it resolves nothing. */
  prop?: { stat: string; playerKey: string } | null;
}

export interface GameEventRow {
  key: string;             // canonicalGameKey or fallback "sport|YYYY-MM-DD|TEAM_A|TEAM_B"
  sport: Sport;
  date: string;
  /** Local-time format of startTimeMs ("7:00p") for the matchup-row display.
   *  Empty when unknown. */
  timePt: string;
  /** Scheduled event start epoch ms — same shape + semantics as GameRow.
   *  Captured from any sub-ticker's eventStartTime. Null when unknown. */
  startTimeMs: number | null;
  /** 'live' = at least one sub-ticker is 'open' AND we've crossed startTimeMs.
   *  'final' = every status seen across this event's sub-tickers is terminal.
   *  'pre' = otherwise. Same derivation as GameRow.gameState — see
   *  marketStore.deriveGameState. */
  gameState: 'live' | 'pre' | 'final';
  /** Source class behind gameState; propagated into synthesized trading rows. */
  gameStateAuthority?: 'venue' | 'schedule' | 'heuristic' | 'unknown';
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  spreads: SpreadLine[];   // sorted by signed line ascending
  totals: TotalLine[];     // sorted ascending by line
}

/**
 * Period (NBA/WNBA 1st-half, soccer 1H/2H, MLB First 5 Innings) moneyline for
 * one game. Unlike the full-game moneyline (binary away/home, surfaced via
 * getGames), the period winner is **3-way on Kalshi** — Home / Away / **TIE** —
 * and **binary on Polymarket** (no tie). The two team rows pair to the single
 * Poly `1h-moneyline` book (slug yes-team native, the other inverted 100−yes);
 * the Tie row is Kalshi-only. Don't assume the outcomes sum to 100 across
 * venues. Built by marketStore.getPeriodMoneyline. (Soccer periods are
 * Kalshi-only in Phase 1 — Polymarket's halftime-result grammar is a Phase 2
 * follow-up. MLB F5 is Kalshi-only outright — Polymarket lists no inning
 * markets — so its away/home/tie books are all Kalshi.)
 */
// ── MLB player props + per-game event markets (mlb-props-events) ────────
// Kalshi-anchored surfaces, period 'full', collapsed onto the game's
// canonical key like every other per-game market. Since poly-props-events
// (2026-07-16) four of the eleven series carry a Polymarket side joined
// CLIENT-SIDE on (canonicalGameKey, stat, line, playerKey): HR/KS props
// (Poly `-hr-`/`-k-`), RFI (`-nrfi`, SAME polarity — no flip) and extras
// (`-extra-innings`). The other seven (team totals, HIT/RBI/TB/SB/OUTS/HRR)
// are Kalshi-only, ever. Built by marketStore.getMlbPlayerProps /
// getMlbEventMarkets; consumed by GameDetail's Player Props / Events tabs.

/** One rung of a yes-ladder: YES resolves at `threshold` or more
 *  (floor_strike = threshold − 0.5 ≡ the Poly XptY line). */
export interface PropRung {
  threshold: number;
  /** Null when only Polymarket lists this threshold (unpaired Poly rung). */
  kalshi: VenueBook | null;
  /** Poly book joined by player slug + line. Static-priced from the Gamma
   *  snapshot (~120s cadence) with NO orderbook — never WS-subscribed on
   *  either engine; hydra's orderbook endpoint 404s prop tickers. Null on
   *  the six Kalshi-only stats and on thresholds Poly doesn't list. */
  polymarket: VenueBook | null;
}

/** One player's threshold ladder within a stat family, rungs ascending. */
export interface PlayerPropLadder {
  /** Stable identity from the Kalshi ticker (`<TEAM><PLAYERCODE>`), or the
   *  Poly player slug (`aj-ewing`) on a Poly-only ladder — the two alphabets
   *  can't collide (upper vs lower/hyphenated). */
  playerKey: string;
  /** op-player-props: present IFF the identity above came from the WIRE
   *  (`HydraMarket.playerKey`, core-minted) — the string the OP
   *  `player-prop` buckets are keyed by. Absent on ticker-derived and
   *  Poly-only ladders, which therefore pin no OP bucket (the cell stays
   *  "—"). When present it EQUALS `playerKey` (spec DNL-002: the ladder key
   *  and the bucket key migrate to the wire field together). */
  opPlayerKey?: string;
  /** Human name from Kalshi's `yes_sub_title` ("Fernando Tatis Jr.") when
   *  forwarded; formatted ticker code ("F. Tatis #23") otherwise. Title-cased
   *  slug on a Poly-only ladder. */
  playerName: string;
  /** '' on a Poly-only ladder — the Poly slug carries no team context. */
  teamCode: string;
  rungs: PropRung[];
}

export interface MlbPropStatGroup {
  /** Stat id — label/order via MLB_PROP_STAT_LABELS / MLB_PROP_STATS
   *  (lib/kalshiTicker/sports/mlb). */
  stat: import('@/lib/kalshiTicker/sports/mlb').MlbPropStat;
  players: PlayerPropLadder[];
}

/** One stat family's ladders for EITHER prop sport (nfl-player-props): the
 *  shape `buildPlayerPropSummaries` and the workbench adapter consume. An
 *  `MlbPropStatGroup` is one of these (its stat is the narrower union); NFL
 *  groups come from `marketStore.getNflPlayerProps`. Labels/order via
 *  `lib/sport/propStat`. On the NFL touchdown binaries every ladder is a
 *  single rung whose threshold is the stat's fixed identity value. */
export interface PlayerPropStatGroup {
  stat: import('@/lib/sport/propStat').PropStat;
  players: PlayerPropLadder[];
}

/** Per-team runs yes-ladder (KXMLBTEAMTOTAL), rungs ascending. */
export interface TeamTotalLadder {
  teamCode: string;
  teamName: string;
  rungs: PropRung[];
}

/** One per-game binary event, both venues. Either side may be null. */
export interface MlbEventBinary {
  kalshi: VenueBook | null;
  polymarket: VenueBook | null;
}

export interface MlbEventMarkets {
  /** Run in the 1st inning — YES = YRFI, NO = NRFI on BOTH venues: the Poly
   *  `-nrfi` slug's question is "Will there be a run scored in the first
   *  inning?" (YES = run scores, live-verified 2026-07-16), the SAME polarity
   *  as Kalshi KXMLBRFI — the join does NOT flip. */
  rfi: MlbEventBinary | null;
  /** Game reaches extra innings (Poly `-extra-innings`, same polarity). */
  extras: MlbEventBinary | null;
  /** Away team's ladder first when the away/home mapping is known.
   *  Kalshi-only — Poly `-team-total-` slugs are not ingested. */
  teamTotals: TeamTotalLadder[];
}

// ── CFB game-event props (cfb-game-props, 2026-08-29) ──────────────────
// Kalshi-only per-game families classified by the WIRE `marketType`
// (lib/cfbWireMarkets), period 'full', collapsed onto the game's canonical
// key. Built by marketStore.getCfbEventMarkets; consumed by GameDetail's
// Events tab for CFB. Poly lists no counterpart for any of them.

/** One YES outcome of an enum family (first to score, 1H/FT result, …). */
export interface CfbEventOutcome {
  ticker: string;
  /** Venue wording (`subtitle`) — "Georgia Tech scores first TD", "NONE",
   *  "Georgia Tech wins 1H / Colorado wins game". */
  label: string;
  /** Which row team the outcome names, when core stamped an honest side;
   *  null on NONE / tie / combo rows. Orders the pane away → home → rest. */
  teamSide: 'away' | 'home' | null;
  teamCode: string | null;
  kalshi: VenueBook;
}

export interface CfbEventEnumGroup {
  cls: import('@/lib/cfbWireMarkets').CfbEnumClass;
  label: string;
  outcomes: CfbEventOutcome[];
}

/** A single yes/no market (overtime, 2-point conversion, a D/ST TD row). */
export interface CfbEventBinary {
  cls: import('@/lib/cfbWireMarkets').CfbBinaryClass;
  ticker: string;
  label: string;
  /** `player-prop` (D/ST TD) is team-sided — the row's team, verified inside
   *  the game's pair (dropped otherwise). Null on overtime / two-point /
   *  every-quarter BTTS. */
  teamSide: 'away' | 'home' | null;
  teamCode: string | null;
  kalshi: VenueBook;
}

export interface CfbOuLine {
  /** The O/U line (`floorStrike`); YES = over. */
  line: number;
  ticker: string;
  kalshi: VenueBook;
}

/** One team's O/U ladder for one stat, lines ascending. */
export interface CfbTeamStatLadder {
  /** `pts` (plain team total) or the wire `stat`. */
  stat: string;
  statLabel: string;
  teamSide: 'away' | 'home';
  teamCode: string;
  /** 'full' on every CFB ladder; NFL team totals also list per period
   *  (nfl-game-lines-props) — one ladder per (stat, team, period). */
  period: Period;
  lines: CfbOuLine[];
}

/** One YES leg of an NFL `race-to` 3-way (away / home / neither). */
export interface RaceToOutcome {
  ticker: string;
  /** Venue wording (`subtitle`). */
  label: string;
  teamSide: 'away' | 'home' | null;
  teamCode: string | null;
  kalshi: VenueBook;
}

/** One points line of the NFL "Race to Points" family, legs away → home →
 *  NONE. Kalshi organises this under Game Lines. */
export interface RaceToLine {
  points: number;
  outcomes: RaceToOutcome[];
}

/** Game-total O/U ladder (touchdowns / field goals), lines ascending. */
export interface CfbTotalLadder {
  cls: import('@/lib/cfbWireMarkets').CfbTotalClass;
  label: string;
  lines: CfbOuLine[];
}

export interface CfbEventMarkets {
  enums: CfbEventEnumGroup[];
  binaries: CfbEventBinary[];
  /** Ordered by stat (lib/cfbWireMarkets order), full game before periods,
   *  away side before home. */
  teamStats: CfbTeamStatLadder[];
  totals: CfbTotalLadder[];
  /** NFL only (nfl-game-lines-props): points lines ascending. Empty until
   *  core ships the `race-to` class. */
  races: RaceToLine[];
  /** NFL only: "both teams score N+" ladder, lines ascending (`line` is the
   *  WHOLE-number wire `floorStrike`). Empty until `both-teams-points` ships. */
  bothTeamsPoints: CfbOuLine[];
  /** Every Kalshi ticker rendered by the tab — the deep-link router's
   *  membership test (no ticker grammar involved). */
  tickers: ReadonlySet<string>;
}

export interface PeriodMoneyline {
  gameKey: string;
  // 'full' = the soccer regulation 1X2, demoted into Game Lines "90 min" when the
  // knockout "To Advance" market owns the top Moneyline slot (other sports use
  // their period winner; full-game lives in getGames there). Quarters are CFB's
  // (KXNCAAF1Q–4Q, 3-way like the halves).
  period: Period;
  awayCode: string;
  homeCode: string;
  away: { kalshi: VenueBook | null; polymarket: VenueBook | null };
  home: { kalshi: VenueBook | null; polymarket: VenueBook | null };
  // NBA/WNBA: Polymarket has no tie outcome (binary 1h-moneyline) → polymarket
  // null. Soccer: Poly lists a draw market (`-halftime-result-draw`), so the tie
  // row can carry a Poly book too.
  tie: { kalshi: VenueBook | null; polymarket: VenueBook | null };
}
